import { useQuery } from '@tanstack/react-query';
import {
  PLAN_CHANGE_DIRECTION_LABELS,
  type PlanChangeRequestForStaff,
  type PlatformMetrics,
  type SubscriberSummary,
} from '@azko/shared';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { formatCurrency } from '../../lib/format.js';
import { useSession } from '../../lib/session.js';
import { Button, Card, EmptyState, StatCard, StatusBadge, cx } from '../../components/ui.js';

interface ChannelHealth {
  channel: string;
  queued: number;
  sending: number;
  failedRecently: number;
  oldestQueuedMinutes: number | null;
  /** Aceitas pelo gateway e ainda sem desfecho confirmado. */
  awaitingConfirmation: number;
  oldestSentMinutes: number | null;
  lastError: string;
}

interface SessionHealth {
  name: string;
  status: string;
  connected: boolean;
}

interface WhatsappHealth {
  /** `false` quando o provider nao tem sessao (Meta, `log`) — nao se alarma. */
  checked: boolean;
  reachable: boolean;
  instances: SessionHealth[];
  error: string;
}

interface PlatformHealth {
  status: 'ok' | 'degraded';
  stuckAfterMinutes: number;
  unconfirmedAfterMinutes: number;
  whatsapp: WhatsappHealth;
  channels: ChannelHealth[];
}

/**
 * Ha por onde mandar?
 *
 * O bloco existe por causa de 10/09: a sessao do numero base caiu, dois avisos
 * do Ville falharam com "Connection Closed", e NENHUMA tela do produto podia
 * dizer o motivo. A fila nao acumulava (o gateway aceita a mensagem), nada
 * falhava do nosso lado, e o morador simplesmente nao recebia. A resposta veio
 * por SSH, seis dias depois.
 *
 * Vem PRIMEIRO, acima da entrega de mensagens, porque e a causa e nao o
 * sintoma: sem sessao viva, tudo o que estiver abaixo e consequencia.
 */
function SessaoDoWhatsapp({ whatsapp }: { whatsapp: WhatsappHealth }) {
  if (!whatsapp.checked) return null;

  const viva = whatsapp.instances.filter((item) => item.connected);
  // Silencio quando esta tudo certo: painel verde permanente vira moldura em
  // duas semanas, e ai deixa de ser lido no dia em que fica vermelho.
  if (whatsapp.reachable && viva.length > 0) return null;

  return (
    <Card className="border-warn/40 p-5 sm:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-bold tracking-tight">WhatsApp fora do ar</h2>
        <StatusBadge tone="warn">
          {whatsapp.reachable ? 'nenhuma sessao conectada' : 'gateway inalcancavel'}
        </StatusBadge>
      </div>

      <p className="mt-2 text-sm text-warn">
        Nenhum morador esta sendo avisado, em nenhum condominio. A portaria continua
        registrando encomenda normalmente — o aviso e que nao sai.
      </p>

      {whatsapp.reachable ? (
        <>
          <ul className="mt-4 space-y-2">
            {whatsapp.instances.map((item) => (
              <li
                key={item.name}
                className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-line bg-surface-2 p-3 text-sm"
              >
                <span className="font-mono">{item.name}</span>
                {/* O estado vai CRU, como o gateway responde: `close` e sessao
                    que caiu, `created` e instancia que nunca foi pareada. Traduzir
                    esconderia um estado novo atras de um rotulo velho. */}
                <StatusBadge tone={item.connected ? 'accent' : 'neutral'}>
                  {item.status}
                </StatusBadge>
              </li>
            ))}
          </ul>

          {whatsapp.instances.length === 0 && (
            <p className="mt-3 text-sm text-muted">
              Nao ha instancia nenhuma neste tenant do gateway.
            </p>
          )}

          <p className="mt-3 text-sm text-muted">
            Sessao so volta parenado o celular de novo — nao ha conserto pelo painel. O passo
            a passo esta em <span className="font-mono">docs/LIGAR-WHATSAPP.md</span>, passo 4.
          </p>
        </>
      ) : (
        <p className="mt-3 break-words text-sm text-faint">
          Nao consegui falar com o gateway: {whatsapp.error}
        </p>
      )}
    </Card>
  );
}

const CHANNEL_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  sms: 'SMS',
  push: 'Push',
};

/**
 * Entrega de mensagens.
 *
 * O bloco existe porque as duas formas de o produto falhar calado moram na
 * outbox: a mensagem que desistiu (o morador nunca soube da encomenda, o
 * sindico nunca recebeu a senha) e a fila que parou de andar. Nenhuma das duas
 * aparecia em tela nenhuma — viravam uma linha de log que ninguem le.
 *
 * So aparece quando ha o que dizer. Um painel verde permanente vira moldura em
 * duas semanas, e ai deixa de ser lido justamente no dia em que fica vermelho.
 */
function EntregaDeMensagens() {
  const { data } = useQuery({
    queryKey: ['admin', 'saude'],
    queryFn: () => api.get<PlatformHealth>('/admin/saude'),
    // A fila anda em segundos; um minuto e o suficiente para a tela nao mentir
    // sem transformar o backoffice aberto num gerador de carga.
    refetchInterval: 60_000,
  });

  if (!data || data.status === 'ok') return null;

  const problemas = data.channels.filter(
    (canal) =>
      canal.failedRecently > 0 ||
      (canal.oldestQueuedMinutes !== null &&
        canal.oldestQueuedMinutes > data.stuckAfterMinutes) ||
      (canal.oldestSentMinutes !== null &&
        canal.oldestSentMinutes > data.unconfirmedAfterMinutes),
  );

  return (
    <>
      <SessaoDoWhatsapp whatsapp={data.whatsapp} />

      {problemas.length > 0 && (
    <Card className="p-5 sm:p-6">
      <h2 className="text-xl font-bold tracking-tight">Entrega de mensagens</h2>
      <p className="mt-1 text-sm text-muted">
        Alguem pode nao ter sido avisado nas ultimas 24 horas — ou foi avisado e ninguem
        confirmou.
      </p>

      <ul className="mt-4 space-y-3">
        {problemas.map((canal) => {
          const parada =
            canal.oldestQueuedMinutes !== null &&
            canal.oldestQueuedMinutes > data.stuckAfterMinutes;

          // Aceita pelo gateway e sem desfecho. E o estado mais enganoso dos
          // tres, porque a tela da encomenda ja diz "morador notificado".
          const semConfirmacao =
            canal.oldestSentMinutes !== null &&
            canal.oldestSentMinutes > data.unconfirmedAfterMinutes;

          return (
            <li key={canal.channel} className="rounded-2xl border border-line bg-surface-2 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-bold">
                  {CHANNEL_LABELS[canal.channel] ?? canal.channel}
                </span>
                {canal.failedRecently > 0 && (
                  <StatusBadge tone="warn">
                    {canal.failedRecently} {canal.failedRecently === 1 ? 'falhou' : 'falharam'}
                  </StatusBadge>
                )}
                {parada && <StatusBadge tone="warn">fila parada</StatusBadge>}
                {semConfirmacao && (
                  <StatusBadge tone="warn">
                    {canal.awaitingConfirmation} sem confirmacao
                  </StatusBadge>
                )}
              </div>

              {parada && (
                <p className="mt-2 text-sm text-muted">
                  {canal.queued} na fila, a mais antiga ha {canal.oldestQueuedMinutes} min. Em
                  operacao normal isso fica abaixo de um minuto — o worker pode estar fora.
                </p>
              )}

              {semConfirmacao && (
                <p className="mt-2 text-sm text-muted">
                  {canal.awaitingConfirmation}{' '}
                  {canal.awaitingConfirmation === 1 ? 'mensagem aceita' : 'mensagens aceitas'}{' '}
                  pelo gateway sem desfecho, a mais antiga ha {canal.oldestSentMinutes} min.
                  Enquanto isso, a encomenda delas diz que o morador foi notificado — e ninguem
                  confirmou. Ou a conferencia parou, ou o gateway nao esta respondendo.
                </p>
              )}

              {canal.lastError && (
                <p className="mt-2 break-words text-sm text-faint">
                  Ultimo erro: {canal.lastError}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
      )}
    </>
  );
}

/**
 * Visao geral do backoffice: o que espera decisao.
 *
 * A pagina existe desde antes de haver telas porque staff nao tem condominio —
 * sem uma casa propria, quem e da equipe logava com sucesso e caia de volta no
 * login, parecendo senha errada. Ela responde a pergunta que a equipe faz ao
 * abrir o sistema: tem alguem esperando por mim?
 *
 * A lista de permissoes ficava aqui e era o maior bloco da tela — inventario
 * do proprio cargo antes do trabalho do dia. Mudou para Configuracoes, onde se
 * consulta quando algo da 403.
 *
 * As duas colunas dividem o dia em decisao (fila) e vigilancia (contas
 * cortadas). Nenhuma delas faz requisicao propria: sao as mesmas queries que
 * as telas de fila e de assinantes ja usam, com a mesma chave de cache.
 */
export function AdminHomePage() {
  const { user, staff } = useSession();

  const podeVerFila = staff?.permissions.includes('plataforma:assinantes:visualizar') ?? false;

  const { data: queue } = useQuery({
    queryKey: ['admin', 'solicitacoes'],
    queryFn: () => api.get<{ items: PlanChangeRequestForStaff[] }>('/admin/solicitacoes'),
    enabled: podeVerFila,
  });

  /**
   * Os numeros da base vem CONTADOS DO BANCO.
   *
   * Antes eles eram somados aqui, a partir da lista de assinantes — que vem
   * paginada. Na conta 101 os quatro cards passavam a sub-reportar em silencio,
   * e a "receita recorrente" somava conta em teste pelo preco cheio, com plano
   * anual entrando como se fosse mensal. Contagem de base nao se faz com a
   * pagina que calhou de estar em maos.
   */
  const { data: metrics } = useQuery({
    queryKey: ['admin', 'metricas'],
    queryFn: () => api.get<PlatformMetrics>('/admin/metricas'),
    enabled: podeVerFila,
  });

  // A lista continua sendo buscada, mas agora para uma coisa so: os nomes das
  // contas cortadas no painel lateral. O numero delas vem das metricas.
  const { data: subscribers } = useQuery({
    queryKey: ['admin', 'assinantes', 'suspended'],
    queryFn: () =>
      api.get<{ items: SubscriberSummary[] }>('/admin/assinantes?status=suspended&limit=8'),
    enabled: podeVerFila,
  });

  if (!staff) return null;

  const pendentes = (queue?.items ?? []).filter((item) => item.status === 'pending');
  const cortadas = subscribers?.items ?? [];
  const totalCortadas = metrics?.accounts.suspended ?? 0;
  const receita = metrics?.revenue;

  return (
    <>
      <div>
        <p className="eyebrow">Equipe Azko</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight">
          Ola, {user?.name.split(' ')[0] ?? 'time'}.
        </h1>
        <p className="mt-2 text-sm text-muted">
          {pendentes.length === 0
            ? 'Nada esperando decisao agora.'
            : `${pendentes.length} ${pendentes.length === 1 ? 'conta espera' : 'contas esperam'} uma resposta sua.`}
        </p>
      </div>

      {podeVerFila && <EntregaDeMensagens />}

      {podeVerFila && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Esperando decisao"
            value={pendentes.length}
            hint={pendentes.length === 0 ? 'fila limpa' : 'trocas de plano'}
            highlight={pendentes.length > 0}
          />
          <StatCard
            label="Contas ativas"
            value={metrics?.accounts.active ?? 0}
            hint="operando normalmente"
          />
          <StatCard
            label="Acesso cortado"
            value={totalCortadas}
            hint={totalCortadas === 0 ? 'nenhuma' : 'sistema parado no cliente'}
          />
          {/* "Receita recorrente" agora e so o que e cobravel. O que esta em
              teste tem card proprio logo abaixo, porque somar os dois foi
              exatamente o que fazia este numero so subir. */}
          <StatCard
            label="Receita recorrente"
            value={formatCurrency(receita?.mrrCents ?? 0, 'BRL')}
            hint={`${receita?.billableCount ?? 0} ${
              (receita?.billableCount ?? 0) === 1 ? 'assinatura cobravel' : 'assinaturas cobraveis'
            }`}
          />
        </div>
      )}

      {/* ----------------------------------------------------------------
          O que ainda nao e receita.
          Fica num bloco proprio e nao virou mais um StatCard na fileira de
          cima de proposito: a fileira de cima e o estado do negocio, e isto
          aqui e uma divida operacional. Toda assinatura nasce `trialing` e
          NADA no sistema a tira de la — nao ha job nem webhook que encerre
          um teste. Enquanto isso for verdade, "teste vencido" e a medida do
          quanto o produto esta sendo usado de graca.
      ---------------------------------------------------------------- */}
      {podeVerFila && (receita?.trialingCount ?? 0) > 0 && (
        <Card
          className={cx(
            'p-5 sm:p-6',
            (receita?.trialExpiredCount ?? 0) > 0 && 'border-warn/40',
          )}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="eyebrow">Ainda nao e receita</p>
              <h2 className="mt-2 text-xl font-bold tracking-tight">
                {receita?.trialingCount} em teste ·{' '}
                {formatCurrency(receita?.trialingCents ?? 0, 'BRL')}/mes em jogo
              </h2>
            </div>
            <Link to="/uso">
              <Button variant="ghost">Ver uso das contas</Button>
            </Link>
          </div>

          {(receita?.trialExpiredCount ?? 0) > 0 ? (
            <p className="mt-3 text-sm text-warn">
              <span className="font-bold">
                {receita?.trialExpiredCount}{' '}
                {receita?.trialExpiredCount === 1 ? 'conta esta' : 'contas estao'} com o teste
                vencido
              </span>{' '}
              e seguem usando o produto. Nada no sistema encerra um teste hoje — o corte so
              acontece por decisao de alguem.
            </p>
          ) : (
            <p className="mt-3 text-sm text-muted">
              Nenhum teste vencido. Lembre que o encerramento e manual: nao ha cobranca
              automatica ligada.
            </p>
          )}

          {(receita?.pastDueCents ?? 0) > 0 && (
            <p className="mt-2 text-sm text-warn">
              {formatCurrency(receita?.pastDueCents ?? 0, 'BRL')}/mes do recorrente esta em
              atraso.
            </p>
          )}
        </Card>
      )}

      {podeVerFila && (
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_24rem]">
          {/* Fila — a coluna larga, porque e onde se decide. */}
          <Card className={cx('p-5 sm:p-6', pendentes.length > 0 && 'border-accent/40')}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="eyebrow">Esperando voce</p>
                <h2 className="mt-2 text-xl font-bold tracking-tight">Trocas de plano</h2>
              </div>
              <Link to="/solicitacoes">
                <Button variant={pendentes.length > 0 ? 'primary' : 'ghost'}>Abrir a fila</Button>
              </Link>
            </div>

            {pendentes.length === 0 ? (
              <EmptyState
                title="Fila limpa"
                description="Nenhum condominio pediu troca de plano no momento."
              />
            ) : (
              <>
                <p className="mt-3 text-sm text-muted">
                  Enquanto ninguem responde, a conta continua no plano antigo — e quem pediu upgrade
                  nao esta pagando o que quer pagar.
                </p>

                <ul className="mt-5 divide-y divide-line-soft border-t border-line-soft">
                  {pendentes.slice(0, 6).map((item) => (
                    <li
                      key={item.id}
                      className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">{item.condominiumName}</span>
                        <span className="block truncate text-sm text-muted">
                          {item.requestedByName ?? 'sindico'} •{' '}
                          {new Date(item.createdAt).toLocaleDateString('pt-BR')}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-3">
                        <span className="text-sm font-semibold tabular-nums">
                          {formatCurrency(item.requestedPriceCents, 'BRL')}/mes
                        </span>
                        <StatusBadge tone={item.direction === 'upgrade' ? 'accent' : 'neutral'}>
                          {PLAN_CHANGE_DIRECTION_LABELS[item.direction]}
                        </StatusBadge>
                      </span>
                    </li>
                  ))}
                </ul>

                {pendentes.length > 6 && (
                  <p className="mt-3 text-sm text-faint">e mais {pendentes.length - 6} na fila.</p>
                )}
              </>
            )}
          </Card>

          {/* Contas cortadas — coluna estreita: e vigilancia, nao trabalho. */}
          <Card className={cx('p-5 sm:p-6', totalCortadas > 0 && 'border-warn/40')}>
            <p className="eyebrow">Acesso cortado</p>
            <h2 className="mt-2 text-xl font-bold tracking-tight">
              {totalCortadas === 0 ? 'Nenhuma conta parada' : `${totalCortadas} paradas`}
            </h2>

            {totalCortadas === 0 ? (
              <p className="mt-3 text-sm text-muted">
                Toda conta da plataforma esta com o sistema no ar.
              </p>
            ) : (
              <>
                <p className="mt-3 text-sm text-muted">
                  Nestes condominios o porteiro nao registra nem entrega encomenda ate alguem
                  liberar.
                </p>
                <ul className="mt-4 space-y-3 border-t border-line-soft pt-4">
                  {cortadas.map((item) => (
                    <li key={item.id} className="text-sm">
                      <span className="block truncate font-semibold">{item.name}</span>
                      <span className="block truncate text-faint">
                        {[item.city, item.state].filter(Boolean).join('/') || 'sem endereco'}
                        {item.planName && ` • ${item.planName}`}
                      </span>
                    </li>
                  ))}
                </ul>
                {totalCortadas > cortadas.length && (
                  <p className="mt-3 text-sm text-faint">
                    e mais {totalCortadas - cortadas.length} nao listadas aqui.
                  </p>
                )}

                <Link to="/assinantes">
                  <Button variant="ghost" className="mt-5">
                    Ver assinantes
                  </Button>
                </Link>
              </>
            )}
          </Card>
        </div>
      )}

      {!podeVerFila && (
        <Card className="p-5 sm:p-6">
          <p className="eyebrow">Sem fila para voce</p>
          <p className="mt-2 text-sm text-muted">
            Seu papel nao alcanca a carteira de assinantes. O que voce pode fazer esta no menu a
            esquerda, e a lista completa das suas permissoes fica em Configuracoes.
          </p>
        </Card>
      )}
    </>
  );
}
